import React, { useEffect, useState } from 'react';
import api from '../api';
import AppHeader from '../components/AppHeader';
import ClientContainer from '../components/ClientContainer';
import ModalDNI from '../components/ModalDNI';
import PaymentMethodsContainer from "../components/PaymentMethodsContainer";
import Navigation from "../components/Navigation";
import BottomNavigation from '../components/BottomNavigation';
import Home from '../components/Home';
import LoansContainer from '../components/LoansContainer';
import FloatingWhatsApp from '../components/FloatingWhatsApp';
import { useBrowserHistory } from '../hooks/useBrowserHistory';
import { getCachedClientData, setCachedClientData, clearExpiredClientCaches } from '../utils/clientCache';
import Swal from 'sweetalert2';

// Development mode configuration
const DEV_MODE = process.env.REACT_APP_DEV_MODE === 'true';

// Mock data for development mode
const getMockClientData = (clientName) => {
    // Get configuration from environment variables with defaults
    const headerColor0 = process.env.REACT_APP_DEV_HEADER_COLOR_0 || '#4A90E2';
    const headerColor1 = process.env.REACT_APP_DEV_HEADER_COLOR_1 || '#357ABD';
    const headerText = process.env.REACT_APP_DEV_HEADER_TEXT || 'white';
    const logoUrl = process.env.REACT_APP_DEV_LOGO_URL || '';
    
    const baseClient = {
        id: 1,
        name: clientName || 'Creditech',
        domain: clientName || 'creditech',
        logo_url: logoUrl,
        configuration: JSON.stringify({
            header_color_0: headerColor0,
            header_color_1: headerColor1,
            header_text: headerText,
            css_url: 'app'
        }),
        whatsapp: '+5491123456789',
        whatsapp_message: 'Hola, necesito ayuda'
    };

    const baseUser = {
        id: 1,
        first_name: 'Usuario',
        last_name: 'Demo',
        name: 'Usuario',
        lastname: 'Demo',
        dni: '12345678',
        email: 'usuario@demo.com'
    };

    const baseAccountInfo = {
        customer: {
            name: 'Usuario Demo',
            identification: {
                dni: '12345678'
            }
        },
        financial: {
            total_debt: 50000,
            overdue_amount: 0,
            current_amount: 15000,
            outstanding_balance: 50000
        },
        next_installments: [
            {
                period: 'Enero 2024',
                amount: 15000,
                installments_count: 1,
                loans_count: 1
            },
            {
                period: 'Febrero 2024',
                amount: 15000,
                installments_count: 1,
                loans_count: 1
            },
            {
                period: 'Marzo 2024',
                amount: 20000,
                installments_count: 2,
                loans_count: 1
            }
        ],
        active_loans_count: 1
    };

    return {
        client: baseClient,
        user: baseUser,
        accountInfo: baseAccountInfo,
        payment_methods: []
    };
};

const Client = () => {
    let uri = window.location.pathname;
    let hash = uri.split("/");
    let token = hash[hash.length-1];
    
    // Get client name from query parameter or environment variable in dev mode
    const urlParams = new URLSearchParams(window.location.search);
    const devClient = DEV_MODE ? (urlParams.get('client') || process.env.REACT_APP_DEV_CLIENT || 'creditech') : null;

    const [nav, setNav] = useState(0);
    const [client, setClient] = useState({});
    const [user, setUser] = useState({});
    const [loans, setLoans] = useState([]);
    const [dni, setDni] = useState(() => {
        return localStorage.getItem(token + 'dni') || '';
    });
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [loading, setLoading] = useState(false);
    const [paymentMethodOpen, setPaymentMethodOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('home');
    const [activeFilters, setActiveFilters] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [accountInfo, setAccountInfo] = useState(null);
    const [error, setError] = useState(false);

    const fetchAccountInfo = async (dni) => {
        if (DEV_MODE) {
            // Return mock account info in dev mode
            const mockData = getMockClientData(devClient);
            setAccountInfo(mockData.accountInfo);
            return;
        }
        
        try {
            const response = await api.get(`/c/${token}/a`, {
                headers: { dni }
            });
            setAccountInfo(response.data.data);
        } catch (error) {
            console.error('Error al obtener información de la cuenta:', error);
        }
    };

    const authenticateUser = async (dni) => {
        // Development mode: auto-authenticate with mock data
        if (DEV_MODE) {
            setLoading(true);
            const mockData = getMockClientData(devClient);
            
            // Simulate async delay
            await new Promise(resolve => setTimeout(resolve, 500));
            
            setDni(dni || '12345678');
            if (dni) {
                localStorage.setItem(token + 'dni', dni);
            }
            
            // Configure client and user
            const clientData = {
                ...mockData.client,
                configuration: mockData.client.configuration ? 
                    JSON.parse(mockData.client.configuration) : 
                    null
            };
            setClient(clientData);
            setUser(mockData.user);
            setPaymentMethods(mockData.payment_methods || []);
            setIsAuthenticated(true);

            // Load custom styles
            if (clientData.configuration?.css_url) {
                const link = document.createElement('link');
                link.href = `/css/${clientData.configuration.css_url}.css`;
                link.rel = 'stylesheet';
                document.head.appendChild(link);
            }

            // Get account information
            await fetchAccountInfo(dni || '12345678');
            
            setLoading(false);
            return true;
        }
        
        // Production mode: check cache first, then authenticate
        try {
            setLoading(true);
            
            // Try to get cached client data
            const cachedClient = getCachedClientData(token);
            
            if (cachedClient) {
                console.log('Using cached client data');
                // Use cached client data immediately (for logo and colors)
                setClient(cachedClient);
                
                // Load custom styles from cache
                if (cachedClient.configuration?.css_url) {
                    const link = document.createElement('link');
                    link.href = `/css/${cachedClient.configuration.css_url}.css`;
                    link.rel = 'stylesheet';
                    document.head.appendChild(link);
                }
            }
            
            // Make API call to authenticate user (still needed for user data and account info)
            const response = await api.get('/c/' + token, { 
                headers: { dni } 
            });
            
            if (response.data.client) {
                setDni(dni);
                localStorage.setItem(token + 'dni', dni);
                
                // Configure client and user
                const clientData = {
                    ...response.data.client,
                    configuration: response.data.client.configuration ? 
                        JSON.parse(response.data.client.configuration) : 
                        null
                };
                
                // Update client state (in case it wasn't cached or changed)
                setClient(clientData);
                setUser(response.data.user);
                setPaymentMethods(response.data.payment_methods || []);
                setIsAuthenticated(true);

                // Cache client data for next time
                setCachedClientData(token, clientData);

                // Load custom styles (if not already loaded from cache)
                if (!cachedClient && clientData.configuration?.css_url) {
                    const link = document.createElement('link');
                    link.href = `/css/${clientData.configuration.css_url}.css`;
                    link.rel = 'stylesheet';
                    document.head.appendChild(link);
                }

                // Get account information
                await fetchAccountInfo(dni);

                return true;
            }
            return false;
        } catch (error) {
            console.error('Error en autenticación:', error);
            if (error.response && error.response.status === 401) {
                console.error('Error de autenticación: DNI incorrecto');
                setError(true);
                resetDni();
                return false;
            }
            return false;
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (value) => {
        const success = await authenticateUser(value);
        if (success) {
            setActiveTab('home');
        }
    };

    // Carga inicial si hay DNI guardado o en modo desarrollo
    useEffect(() => {
        // Clear expired caches on app load
        clearExpiredClientCaches();
        
        if (DEV_MODE) {
            // Auto-authenticate in dev mode
            authenticateUser('12345678');
        } else if (dni) {
            authenticateUser(dni);
        }
    }, []);

    const handleNavigate = (key) => {
        setNav(key);
    };

    const resetDni = () => {
        setDni('');
        setIsAuthenticated(false);
        setClient({});
        setUser({});
        setLoans([]);
        setPaymentMethods([]);
        localStorage.removeItem(token + 'dni');
    };

    const openPaymentMethod = (status) => {
        setPaymentMethodOpen(status);
    }

    const fetchLoans = (pageNum = 1, filters = activeFilters) => {
        // In dev mode, return empty loans array
        if (DEV_MODE) {
            setLoans({ loans: [], pagination: { current_page: 1, total_pages: 1 } });
            setHasMore(false);
            setPage(1);
            return;
        }
        
        // setLoading(true);
        api.get(`/c/${token}/l`, {
            headers: { dni: dni },
            params: {
                page: pageNum,
                status: filters.join(',')
            }
        })
        .then(response => {
            const { data, pagination } = response.data.data;
            if (pageNum === 1) {
                setLoans(response.data.data);
            } else {
                setLoans(prevLoans => ({
                    ...prevLoans,
                    loans: [...prevLoans.loans, ...data.loans]
                }));
            }
            setHasMore(pagination.current_page < pagination.total_pages);
            setPage(pageNum);
        })
        .catch(error => {
            console.error('Error:', error);
        })
        .finally(() => {
            // setLoading(false);
        });
    };

    const handleLoadMore = () => {
        fetchLoans(page + 1);
    };

    const handleFilterChange = (newFilters) => {
        setActiveFilters(newFilters);
        setPage(1);
        fetchLoans(1, newFilters);
    };

    // Handle logout modal (same as AppHeader)
    const handleLogout = React.useCallback(() => {
        Swal.fire({
            title: '¿Cerrar sesión?',
            text: "Se cerrará la ventana actual",
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Sí, cerrar sesión',
            cancelButtonText: 'Cancelar'
        }).then((result) => {
            if (result.isConfirmed) {
                resetDni();
                window.close();
            }
        });
    }, [resetDni]);

    // Handle browser back/forward navigation for tabs
    const handleBrowserStateChange = (newState) => {
        if (!isAuthenticated) return;
        
        // Check if it's a loan detail state - switch to loans tab first
        if (newState.startsWith('loan-detail-')) {
            setActiveTab('loans');
            // LoansContainer will handle the detail navigation
            return;
        }
        
        // Check if it's a payment method state - switch to payments tab first
        if (newState.startsWith('payment-method-')) {
            setActiveTab('payments');
            // PaymentMethodsContainer will handle the method navigation
            return;
        }
        
        // Handle tab navigation
        if (['home', 'loans', 'payments'].includes(newState)) {
            setActiveTab(newState);
        }
    };

    // Handle browser back when in Home - show logout modal
    useEffect(() => {
        if (!isAuthenticated || activeTab !== 'home') return;

        const handlePopState = (event) => {
            // If we're in home and there's no state or state is 'home', show logout modal
            if (!event.state || !event.state.reactState || event.state.reactState === 'home') {
                // Push state back immediately to prevent navigation
                window.history.pushState({ reactState: 'home' }, '', window.location.href);
                // Show logout modal
                handleLogout();
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [isAuthenticated, activeTab, handleLogout]);

    // Listen for navigate-to-home event from LoansContainer or PaymentMethodsContainer
    useEffect(() => {
        if (!isAuthenticated) return;

        const handleNavigateToHome = () => {
            setActiveTab('home');
        };

        window.addEventListener('navigate-to-home', handleNavigateToHome);
        return () => {
            window.removeEventListener('navigate-to-home', handleNavigateToHome);
        };
    }, [isAuthenticated]);

    // Track history for all tabs
    // When in loans tab, LoansContainer will also track its own history
    // but we still need to track tab changes
    const getHistoryState = () => {
        if (!isAuthenticated) return 'home';
        // When in loans tab, use 'loans' as state (LoansContainer handles detail states)
        // When in payments tab, use 'payments' as state (PaymentMethodsContainer handles method states)
        return activeTab;
    };

    // Track history for all tabs except home
    // Home has custom handling for back button
    useBrowserHistory(
        getHistoryState(),
        handleBrowserStateChange,
        isAuthenticated && activeTab !== 'home'
    );

    // Initialize history with home state when authenticated
    useEffect(() => {
        if (!isAuthenticated) return;
        
        // If there's no state in history, initialize with home
        if (!window.history.state || !window.history.state.reactState) {
            window.history.replaceState({ reactState: 'home' }, '', window.location.href);
        }
    }, [isAuthenticated]);

    return (
        <>
            <AppHeader 
                client={client} 
                user={user}
                loading={loading} 
                resetDni={resetDni}
                goToHome={() => setActiveTab('home')}
                isAuthenticated={isAuthenticated}
            />
            
            <main className="pb-5" style={{ paddingTop: '80px' }}>
                <div className="container mb-5">
                    {!isAuthenticated ? (
                        <Home />
                    ) : (
                        <>
                            {activeTab === 'home' && (
                                <Home 
                                    accountInfo={accountInfo} 
                                    client={client} 
                                    user={user}
                                    onNavigate={setActiveTab}
                                />
                            )}
                            
                            {activeTab === 'loans' && (
                                <LoansContainer token={token} client={client} />
                            )}
                            
                            {activeTab === 'payments' && (
                                <PaymentMethodsContainer client={client} onNavigate={setActiveTab} />
                            )}
                        </>
                    )}
                </div>
            </main>

            <BottomNavigation 
                activeTab={activeTab}
                onTabChange={setActiveTab}
                isAuthenticated={isAuthenticated}
            />
            
            {!DEV_MODE && <ModalDNI dni={dni} onSubmit={handleSubmit} error={error} />}

            {isAuthenticated && <FloatingWhatsApp client={client} />}
        </>
    );
};

export default Client;
